/**
 * The studios list (D25's admin screen): name, id, site, version and who
 * wrote it; open one (the studio page), create one (`device.configure`).
 * `studios`, `loading` and `error` are independent props.
 */
import type { StudioSummaryWire } from '@norna/api-contract/studios.js';
import { PlusIcon, RefreshCwIcon } from 'lucide-react';
import { isPlainClick } from '../../lib/links.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';

export type StudioListProps = {
  readonly studios: readonly StudioSummaryWire[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly canCreate: boolean;
  readonly onCreate: () => void;
  readonly onRetry: () => void;
  readonly onOpen: (studioId: string) => void;
};

export function studioHref(studioId: string): string {
  return `/studios/${encodeURIComponent(studioId)}`;
}

export function StudioList({ studios, loading, error, canCreate, onCreate, onRetry, onOpen }: StudioListProps) {
  return (
    <Card data-testid="studio-list">
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <div className="grid gap-1.5">
          <CardTitle>Studios</CardTitle>
          <CardDescription>Each studio’s devices, mappings, layers, piece types and panels — the config a production snapshots.</CardDescription>
        </div>
        {canCreate ? (
          <Button onClick={onCreate} data-testid="create-studio">
            <PlusIcon />
            New studio
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        {error === null ? null : (
          <div role="alert" className="flex items-center justify-between gap-4 rounded-md border border-destructive p-4 text-sm">
            <span className="text-destructive">Could not load studios: {error}</span>
            <Button variant="outline" size="sm" onClick={onRetry}>
              <RefreshCwIcon />
              Retry
            </Button>
          </div>
        )}
        {loading && studios.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">Loading studios…</p> : null}
        {!loading && error === null && studios.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground" data-testid="studios-empty">
            No studios yet.{canCreate ? ' Create one from the template to get started.' : ''}
          </p>
        ) : null}
        {studios.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Id</TableHead>
                <TableHead>Site</TableHead>
                <TableHead className="w-24">Version</TableHead>
                <TableHead>Last written by</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {studios.map((s) => (
                <TableRow key={s.studioId} data-testid="studio-row">
                  <TableCell className="font-medium">
                    <a
                      href={studioHref(s.studioId)}
                      className="hover:underline"
                      data-testid="open-studio"
                      onClick={(event) => {
                        if (!isPlainClick(event)) return;
                        event.preventDefault();
                        onOpen(s.studioId);
                      }}
                    >
                      {s.name}
                    </a>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{s.studioId}</TableCell>
                  <TableCell className="text-sm">{s.siteId ?? '–'}</TableCell>
                  <TableCell className="font-mono text-sm">v{s.version}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{s.updatedBy}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
      </CardContent>
    </Card>
  );
}
